// The eye branch's assets: one EyeAssets shape from either source, so the two mounts cannot drift.

import { buildEyeCharts, loadEyeCharts } from './eyeChart.ts'
import type { EyeChart, EyeMeta } from './eyeChart.ts'
import { loadHeadEyeMesh, loadHeadEyeMeshFromBytes } from './headEyeMesh.ts'
import type { HeadEyeMesh } from './headEyeMesh.ts'
import type { EyeSide } from './eyeSide.ts'
import type { EyeDecoderUrls } from './eyeDecoders.ts'

export type EyeAssets = {
  mesh: HeadEyeMesh
  charts: Record<EyeSide, EyeChart>
  meta: EyeMeta
  /** ONNX bytes or URLs — ORT takes either, so neither loader has to fetch models. */
  models: EyeDecoderUrls
}

/** The `branches.eyes` entry of a parsed scene manifest. */
export type EyeBranchSpec = {
  statics?: { leftBin: string, leftJson: string, rightBin: string, rightJson: string, meta: string }
  eyeMesh?: { glb: string, json: string }
  trunk?: string
  geom: string
  appr: string
  lookAtCamera?: boolean
}

export type BundleBytes = (name: string) => Uint8Array | undefined

/** The mesh is REQUIRED: it is the geometry, since the decoders emit no positions. */
export async function loadEyeAssetsFromBundle(spec: EyeBranchSpec,
                                              getBytes: BundleBytes): Promise<EyeAssets> {
  const statics = spec?.statics
  if (!statics) throw new Error('eyes: branch declares no statics')
  if (!spec.trunk) throw new Error('eyes: branch declares no trunk')
  if (!spec.eyeMesh?.glb) {
    throw new Error('eyes: branch declares no eyeMesh (mesh_glb/mesh_json) — re-export the '
      + 'bundle with the head+eyes GLB')
  }

  const rawMeta = readJson(getBytes, statics.meta)
  const { charts, meta } = buildEyeCharts(
    {
      left: readArrayBuffer(getBytes, statics.leftBin),
      right: readArrayBuffer(getBytes, statics.rightBin),
    },
    {
      left: readJson(getBytes, statics.leftJson),
      right: readJson(getBytes, statics.rightJson),
    },
    rawMeta)

  const mesh = await loadHeadEyeMeshFromBytes(
    readArrayBuffer(getBytes, spec.eyeMesh.glb),
    readJson(getBytes, spec.eyeMesh.json))

  return {
    mesh,
    charts,
    meta,
    models: {
      trunk: requireBytes(getBytes, spec.trunk),
      geom: requireBytes(getBytes, spec.geom),
      appr: requireBytes(getBytes, spec.appr),
    },
  }
}

export type EyeAssetUrls = {
  meshGlb: string
  meshJson: string
  leftBin: string
  leftJson: string
  rightBin: string
  rightJson: string
  meta: string
  trunk: string
  geom: string
  appr: string
}

export async function loadEyeAssetsFromUrls(urls: EyeAssetUrls): Promise<EyeAssets> {
  const [mesh, { charts, meta }] = await Promise.all([
    loadHeadEyeMesh(urls.meshGlb, urls.meshJson),
    loadEyeCharts(urls),
  ])
  return { mesh, charts, meta, models: { trunk: urls.trunk, geom: urls.geom, appr: urls.appr } }
}

function requireBytes(getBytes: BundleBytes, name: string): Uint8Array {
  const bytes = getBytes(name)
  if (!bytes) throw new Error(`eyes: bundle is missing ${name}`)
  return bytes
}

function readJson(getBytes: BundleBytes, name: string): any {
  return JSON.parse(new TextDecoder().decode(requireBytes(getBytes, name)))
}

function readArrayBuffer(getBytes: BundleBytes, name: string): ArrayBuffer {
  const bytes = requireBytes(getBytes, name)
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}
